import {
  PRE_START_RESPONSE_TYPES_V3,
  responseConfigToResponseTypeValueV3
} from "../pre-start/preStartQuestionResponseConfigUtilsV3";

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const areEqual = (left, right) => {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (error) {
    return left === right;
  }
};


const getSectionData = (values = {}, sectionKey) => (
  values
  && values.sections
  && values.sections[sectionKey]
  && values.sections[sectionKey].data
    ? values.sections[sectionKey].data
    : {}
);

export const buildSwmsSectionOrderChanges = (initialValues = {}, submittedValues = {}) => {
  const initialOrder = initialValues.sections && Array.isArray(initialValues.sections.active_order)
    ? initialValues.sections.active_order
    : [];
  const submittedOrder = submittedValues.sections && Array.isArray(submittedValues.sections.active_order)
    ? submittedValues.sections.active_order
    : [];
  const sameSections = initialOrder.length === submittedOrder.length
    && initialOrder.every((sectionKey) => submittedOrder.includes(sectionKey));

  if (!sameSections || areEqual(initialOrder, submittedOrder)) {
    return [];
  }

  const initialPositions = new Map(initialOrder.map((sectionKey, index) => [sectionKey, index + 1]));
  const movedKeys = new Set(
    submittedOrder.filter((sectionKey, index) => initialPositions.get(sectionKey) !== index + 1)
  );
  const getSectionLabel = (values, sectionKey) => (
    values.sections
    && values.sections[sectionKey]
    && values.sections[sectionKey].display_name
  ) || sectionKey;

  return [{
    item_id: null,
    item_label: null,
    section_changes: [{
      section_key: "meta.sections.active",
      section_label: "Swms Sections",
      changes: [{
        field_name: "active_section_order",
        field_label: "Section Order",
        change_type: "multiple_items",
        action: "reordered",
        previous_value: initialOrder
          .map((sectionKey, index) => ({
            id: sectionKey,
            label: getSectionLabel(initialValues, sectionKey),
            position: index + 1
          }))
          .filter((section) => movedKeys.has(section.id)),
        new_value: submittedOrder
          .map((sectionKey, index) => ({
            id: sectionKey,
            label: getSectionLabel(submittedValues, sectionKey),
            position: index + 1
          }))
          .filter((section) => movedKeys.has(section.id))
      }]
    }]
  }];
};

const getPreStartHeading = (checklist = {}) => (
  checklist
  && checklist.data
  && typeof checklist.data.prestart_checklist_heading === "string"
    ? checklist.data.prestart_checklist_heading
    : ""
);

const getPreStartStaffSignoff = (checklist = {}) => (
  checklist
  && checklist.data
  && typeof checklist.data.prestart_staff_signoff === "string"
    ? checklist.data.prestart_staff_signoff
    : ""
);

const getResponseTypeHistoryValue = (responseConfig = {}) => {
  const value = responseConfigToResponseTypeValueV3(responseConfig);
  const option = PRE_START_RESPONSE_TYPES_V3.find((responseType) => responseType.value === value);

  return option
    ? { value: option.value, label: option.label }
    : (value ? { value, label: value } : null);
};

const getFlagHistoryValue = (responseConfig = {}) => {
  const value = responseConfig && responseConfig.flagOnValue ? responseConfig.flagOnValue : "";
  const option = Array.isArray(responseConfig && responseConfig.options)
    ? responseConfig.options.find((responseOption) => responseOption && responseOption.value === value)
    : null;

  return {
    value,
    label: option && option.label ? option.label : "None"
  };
};

export const buildPreStartEditItems = (initialChecklist = {}, submittedChecklist = {}) => {
  const previousValue = getPreStartHeading(initialChecklist);
  const newValue = getPreStartHeading(submittedChecklist);
  const previousStaffSignoff = getPreStartStaffSignoff(initialChecklist);
  const newStaffSignoff = getPreStartStaffSignoff(submittedChecklist);
  const initialQuestions = initialChecklist
    && initialChecklist.data
    && Array.isArray(initialChecklist.data.pre_start_questions)
    ? initialChecklist.data.pre_start_questions
    : [];
  const submittedQuestions = submittedChecklist
    && submittedChecklist.data
    && Array.isArray(submittedChecklist.data.pre_start_questions)
    ? submittedChecklist.data.pre_start_questions
    : [];
  const items = [];
  const initialById = new Map(initialQuestions.map((question) => [question.id, question]));
  const submittedById = new Map(submittedQuestions.map((question) => [question.id, question]));
  const questionIds = new Set([...initialById.keys(), ...submittedById.keys()]);

  const createQuestionItem = (question, changes) => ({
    item_id: null,
    item_label: null,
    section_changes: [{
      section_key: `question_${question && question.id !== undefined ? question.id : ""}`,
      section_label: question && question.question_text ? question.question_text : "",
      changes
    }]
  });

  questionIds.forEach((questionId) => {
    const previous = initialById.get(questionId);
    const next = submittedById.get(questionId);
    if (!previous || !next) {
      const question = next || previous;
      if (next) {
        items.push({
          item_id: null,
          item_label: null,
          section_changes: [{
            section_key: "pre_start_questions",
            section_label: "Checklist Questions",
            changes: [{
              field_name: "question",
              field_label: "Question",
              change_type: "summary",
              action: "added",
              previous_value: null,
              new_value: next
            }]
          }]
        });
        return;
      }

      items.push(createQuestionItem(question, [{
        field_name: "question",
        field_label: "Question",
        change_type: "summary",
        action: next ? "added" : "deleted",
        previous_value: next ? null : previous,
        new_value: next ? next : null
      }]));
      return;
    }

    const changes = [];
    const addUpdated = (fieldName, fieldLabel, changeType = "inline") => {
      if (!areEqual(previous[fieldName], next[fieldName])) {
        changes.push({
          field_name: fieldName,
          field_label: fieldLabel,
          change_type: changeType,
          action: "updated",
          previous_value: previous[fieldName] === undefined ? null : previous[fieldName],
          new_value: next[fieldName] === undefined ? null : next[fieldName]
        });
      }
    };
    const addToggle = (fieldName, fieldLabel) => {
      const previousValue = Boolean(previous[fieldName]);
      const newValue = Boolean(next[fieldName]);
      if (previousValue !== newValue) {
        changes.push({
          field_name: fieldName,
          field_label: fieldLabel,
          change_type: "inline",
          action: newValue ? "toggled_on" : "toggled_off",
          previous_value: previousValue,
          new_value: newValue
        });
      }
    };
    const addResponseConfigChange = (fieldName, fieldLabel, previousValue, newValue) => {
      if (!areEqual(previousValue, newValue)) {
        changes.push({
          field_name: `response_config.${fieldName}`,
          field_label: fieldLabel,
          change_type: "inline",
          action: "updated",
          previous_value: previousValue,
          new_value: newValue
        });
      }
    };

    addUpdated("question_text", "Question Text");
    addUpdated("guidance_text", "Guidance Text");
    addUpdated("answer_scope", "Answer Scope");
    addToggle("required", "Required");
    addToggle("active", "Question Status");
    addResponseConfigChange(
      "response_type",
      "Response Type",
      getResponseTypeHistoryValue(previous.response_config),
      getResponseTypeHistoryValue(next.response_config)
    );
    addResponseConfigChange(
      "flagOnValue",
      "Flag Value",
      getFlagHistoryValue(previous.response_config),
      getFlagHistoryValue(next.response_config)
    );
    addResponseConfigChange(
      "unit",
      "Unit",
      previous.response_config && Object.prototype.hasOwnProperty.call(previous.response_config, "unit")
        ? previous.response_config.unit
        : null,
      next.response_config && Object.prototype.hasOwnProperty.call(next.response_config, "unit")
        ? next.response_config.unit
        : null
    );
    const previousMethod = {
      method_id: previous.method_id === undefined ? null : previous.method_id,
      method_name: previous.method_name === undefined ? null : previous.method_name
    };
    const newMethod = {
      method_id: next.method_id === undefined ? null : next.method_id,
      method_name: next.method_name === undefined ? null : next.method_name
    };
    if (!areEqual(previousMethod, newMethod)) {
      changes.push({
        field_name: "method",
        field_label: "Method",
        change_type: "inline",
        action: "updated",
        previous_value: previousMethod,
        new_value: newMethod
      });
    }

    if (changes.length > 0) {
      items.push(createQuestionItem(next, changes));
    }
  });

  const initialOrder = initialQuestions.map((question) => question.id);
  const submittedOrder = submittedQuestions.map((question) => question.id);
  const sameQuestionSet = initialOrder.length === submittedOrder.length
    && initialOrder.every((questionId) => submittedOrder.includes(questionId));
  if (sameQuestionSet && !areEqual(initialOrder, submittedOrder)) {
    const initialPositions = new Map(initialOrder.map((questionId, index) => [questionId, index + 1]));
    const movedIds = new Set(
      submittedOrder.filter((questionId, index) => initialPositions.get(questionId) !== index + 1)
    );
    items.push({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "pre_start_questions",
        section_label: "Checklist Questions",
        changes: [{
          field_name: "question_order",
          field_label: "Question Order",
          change_type: "multiple_items",
          action: "reordered",
          previous_value: initialQuestions
            .map((question, index) => ({
              id: question.id,
              label: question.question_text,
              position: index + 1
            }))
            .filter((question) => movedIds.has(question.id)),
          new_value: submittedQuestions
            .map((question, index) => ({
              id: question.id,
              label: question.question_text,
              position: index + 1
            }))
            .filter((question) => movedIds.has(question.id))
        }]
      }]
    });
  }

  const headingChanges = [];
  if (!areEqual(previousValue, newValue)) {
    headingChanges.push({
      field_name: "prestart_checklist_heading",
      field_label: "Checklist Questions Heading",
      change_type: "rich_text",
      action: "updated",
      previous_value: previousValue,
      new_value: newValue
    });
  }
  if (!areEqual(previousStaffSignoff, newStaffSignoff)) {
    headingChanges.push({
      field_name: "prestart_staff_signoff",
      field_label: "Pre-Start Staff Signoff Heading",
      change_type: "rich_text",
      action: "updated",
      previous_value: previousStaffSignoff,
      new_value: newStaffSignoff
    });
  }

  if (headingChanges.length > 0) {
    items.unshift({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "pre_start_questions",
        section_label: "Checklist Questions",
        changes: headingChanges
      }]
    });
  }

  return items;
};

export const buildPreStartToggleEditItems = (initialChecklist = {}, submittedChecklist = {}) => {
  const previousValue = Boolean(initialChecklist && initialChecklist.toggle);
  const newValue = Boolean(submittedChecklist && submittedChecklist.toggle);

  if (previousValue === newValue) {
    return [];
  }

  return [{
    item_id: null,
    item_label: null,
    section_changes: [{
      section_key: "pre_start_checklist",
      section_label: "Pre-Start Checklist",
      changes: [{
        field_name: "toggle",
        field_label: "Pre-Start Checklist Toggle",
        change_type: "inline",
        action: newValue ? "toggled_on" : "toggled_off",
        previous_value: previousValue,
        new_value: newValue
      }]
    }]
  }];
};

const getEmergencyPlanId = (plan = {}) => (
  plan.document_number !== undefined && plan.document_number !== null
    ? plan.document_number
    : null
);

const getPlanCategoriesForHistory = (plan = {}) => {
  const categories = Array.isArray(plan.categories) ? plan.categories : [];

  return categories
    .filter((category) => category && (category.id !== undefined || category.value !== undefined))
    .map((category) => ({
      id: category.id !== undefined ? category.id : category.value,
      label: category.category_name || category.name || category.label || `${category.id !== undefined ? category.id : category.value}`
    }))
    .sort((left, right) => `${left.id}`.localeCompare(`${right.id}`));
};

const getCategoryChangeAction = (previousCategories, newCategories) => {
  if (previousCategories.length === 0 && newCategories.length > 0) {
    return "added";
  }

  if (previousCategories.length > 0 && newCategories.length === 0) {
    return "deleted";
  }

  return "updated";
};

const getEmergencyPlanAuthorisationNotes = (plan = {}) => (
  typeof plan.emergency_plans_authorisation_notes === "string"
    ? plan.emergency_plans_authorisation_notes
    : (typeof plan.authorisation_note === "string" ? plan.authorisation_note : "")
);

const getEmergencyPlanSection = (plan = {}, sectionKey) => {
  if (Array.isArray(plan.sections)) {
    return plan.sections.find((section) => section && (section.id || section.key) === sectionKey) || {};
  }

  return plan.sections && plan.sections[sectionKey] ? plan.sections[sectionKey] : {};
};

const getBooleanValue = (value) => value === true || value === 1 || value === "1";

const getRescueProcedureId = (procedure = {}) => (
  procedure.procedure_id !== undefined
    ? procedure.procedure_id
    : (procedure.id !== undefined ? procedure.id : procedure.document_number)
);

const getRescueProcedureHistoryValue = (procedure = {}, checked) => ({
  procedure_title: procedure.procedure_title || procedure.title || "",
  document_number: procedure.document_number || "",
  procedure_id: procedure.procedure_id !== undefined
    ? procedure.procedure_id
    : (procedure.id !== undefined ? procedure.id : null),
  checked
});

const getRescueProcedureLabel = (procedure = {}) => (
  procedure.procedure_title || procedure.title || procedure.document_number || `${getRescueProcedureId(procedure)}`
);

const getLinkedDocuments = (plan = {}) => (
  Array.isArray(plan.linked_documents) ? plan.linked_documents : []
);

const getRescueProcedurePositions = (plan = {}) => getLinkedDocuments(plan)
  .filter((procedure) => getBooleanValue(procedure.checked))
  .map((procedure, index) => ({
    procedure,
    position: Number.isFinite(Number(procedure.display_order))
      ? Number(procedure.display_order)
      : index + 1
  }))
  .sort((left, right) => left.position - right.position);

const RESCUE_EQUIPMENT_PARTS = [
  { key: "rescue_equipment", label: "Rescue Equipment" },
  { key: "rescue_ppe", label: "Rescue PPE" },
  { key: "licences_competencies", label: "Licences & Competencies" }
];

const getRescueEquipmentSection = (plan = {}) => getEmergencyPlanSection(plan, "rescue_equipment_staging");

const getMethodItemTypeHistoryValue = (part = {}) => ({
  id: part.method_item_type_id !== undefined && part.method_item_type_id !== null
    ? part.method_item_type_id
    : (part.method_item_type && part.method_item_type.value !== undefined
      ? part.method_item_type.value
      : null),
  label: typeof part.method_item_type === "string"
    ? part.method_item_type
    : (part.method_item_type && part.method_item_type.label) || ""
});

const isCustomMethodItem = (item = {}) => item.isNew === true || item.is_new === 1;

const getMethodItemHistoryValue = (item = {}) => ({
  id: item.id,
  method_item: item.method_item || item.name || "",
  ...(isCustomMethodItem(item) ? { isNew: true, is_new: 1 } : {})
});

const buildRescueEquipmentPartChanges = (initialPart = {}, submittedPart = {}, partLabel) => {
  const changes = [];
  const previousType = getMethodItemTypeHistoryValue(initialPart);
  const newType = getMethodItemTypeHistoryValue(submittedPart);

  if (!areEqual(previousType, newType)) {
    changes.push({
      field_name: "method_item_type",
      field_label: `${partLabel} Method Item Type`,
      change_type: "inline",
      action: "updated",
      previous_value: previousType,
      new_value: newType
    });
  }

  const initialItems = Array.isArray(initialPart.method_items) ? initialPart.method_items : [];
  const submittedItems = Array.isArray(submittedPart.method_items) ? submittedPart.method_items : [];
  const initialById = new Map(initialItems.map((item) => [`${item && item.id}`, item]));
  const submittedById = new Map(submittedItems.map((item) => [`${item && item.id}`, item]));
  const added = [];
  const deleted = [];
  const updated = [];
  const itemIds = new Set([...initialById.keys(), ...submittedById.keys()]);

  itemIds.forEach((itemId) => {
    const previous = initialById.get(itemId);
    const next = submittedById.get(itemId);
    if (!previous || !next) {
      (next ? added : deleted).push(getMethodItemHistoryValue(next || previous));
      return;
    }

    if (isCustomMethodItem(previous) && isCustomMethodItem(next)
      && !areEqual(previous.method_item, next.method_item)) {
      updated.push({
        previous: getMethodItemHistoryValue(previous),
        next: getMethodItemHistoryValue(next)
      });
    }
  });

  if (added.length > 0) {
    changes.push({
      field_name: "method_items",
      field_label: partLabel,
      change_type: "multiple_items",
      action: "added",
      previous_value: [],
      new_value: added
    });
  }
  if (deleted.length > 0) {
    changes.push({
      field_name: "method_items",
      field_label: partLabel,
      change_type: "multiple_items",
      action: "deleted",
      previous_value: deleted,
      new_value: []
    });
  }
  if (updated.length > 0) {
    changes.push({
      field_name: "method_items",
      field_label: partLabel,
      change_type: "multiple_items",
      action: "updated",
      previous_value: updated.map((entry) => entry.previous),
      new_value: updated.map((entry) => entry.next)
    });
  }

  return changes;
};

const buildRescueEquipmentStagingChanges = (initialPlan = {}, submittedPlan = {}) => {
  const initialSection = getRescueEquipmentSection(initialPlan);
  const submittedSection = getRescueEquipmentSection(submittedPlan);
  const sectionLabel = submittedSection.display_name
    || initialSection.display_name
    || "Rescue Equipment and Staging";
  const sectionChanges = [];
  const previousToggle = getBooleanValue(initialSection.data && initialSection.data.toggle);
  const newToggle = getBooleanValue(submittedSection.data && submittedSection.data.toggle);

  if (previousToggle !== newToggle) {
    sectionChanges.push({
      section_key: "rescue_equipment_staging",
      section_label: sectionLabel,
      changes: [{
        field_name: "toggle",
        field_label: `${sectionLabel} Toggle`,
        change_type: "inline",
        action: newToggle ? "toggled_on" : "toggled_off",
        previous_value: previousToggle,
        new_value: newToggle
      }]
    });
  }

  RESCUE_EQUIPMENT_PARTS.forEach((part) => {
    const partChanges = buildRescueEquipmentPartChanges(
      (initialSection.data && initialSection.data[part.key]) || {},
      (submittedSection.data && submittedSection.data[part.key]) || {},
      part.label
    );
    if (partChanges.length > 0) {
      sectionChanges.push({
        section_key: "rescue_equipment_staging",
        section_label: sectionLabel,
        changes: partChanges
      });
    }
  });

  return sectionChanges;
};

const getRolesSection = (plan = {}) => getEmergencyPlanSection(plan, "roles_responsibilities_competency");

const getRolesItemLabel = (item = {}) => item.role || item.responsibility || item.competency || "Role";

const getRolesItemValue = (item = {}) => ({
  _history_id: item._history_id,
  role: item.role || "",
  responsibility: item.responsibility || "",
  competency: item.competency || ""
});

const buildRolesChanges = (initialPlan = {}, submittedPlan = {}) => {
  const initialSection = getRolesSection(initialPlan);
  const submittedSection = getRolesSection(submittedPlan);
  const sectionLabel = submittedSection.display_name
    || initialSection.display_name
    || "Roles, Responsibilities & Competency";
  const changes = [];
  const previousToggle = getBooleanValue(initialSection.data && initialSection.data.toggle);
  const newToggle = getBooleanValue(submittedSection.data && submittedSection.data.toggle);

  if (previousToggle !== newToggle) {
    changes.push({
      field_name: "toggle",
      field_label: `${sectionLabel} Toggle`,
      change_type: "inline",
      action: newToggle ? "toggled_on" : "toggled_off",
      previous_value: previousToggle,
      new_value: newToggle
    });
  }

  const initialItems = Array.isArray(initialSection.data && initialSection.data.items)
    ? initialSection.data.items
    : [];
  const submittedItems = Array.isArray(submittedSection.data && submittedSection.data.items)
    ? submittedSection.data.items
    : [];
  const initialById = new Map(initialItems.map((item) => [item._history_id, item]));
  const submittedById = new Map(submittedItems.map((item) => [item._history_id, item]));
  const itemIds = new Set([...initialById.keys(), ...submittedById.keys()]);
  const added = [];
  const deleted = [];
  const updated = [];

  itemIds.forEach((itemId) => {
    const previous = initialById.get(itemId);
    const next = submittedById.get(itemId);
    if (!previous || !next) {
      (next ? added : deleted).push(getRolesItemValue(next || previous));
      return;
    }

    if (!areEqual(getRolesItemValue(previous), getRolesItemValue(next))) {
      updated.push({
        previous: getRolesItemValue(previous),
        next: getRolesItemValue(next)
      });
    }
  });

  added.forEach((item) => {
    changes.push({
      field_name: "items",
      field_label: `Added - ${item.role || "Empty Role"}`,
      change_type: "summary",
      action: "added",
      previous_value: null,
      new_value: item
    });
  });
  deleted.forEach((item) => {
    changes.push({
      field_name: "items",
      field_label: `Removed - ${item.role || "Empty Role"}`,
      change_type: "summary",
      action: "deleted",
      previous_value: item,
      new_value: null
    });
  });
  updated.forEach((entry) => {
    changes.push({
      field_name: "items",
      field_label: `Updated - ${entry.next.role || entry.previous.role || "Empty Role"}`,
      change_type: "multiple_items",
      action: "updated",
      previous_value: entry.previous,
      new_value: entry.next
    });
  });

  const initialOrder = initialItems.map((item) => item._history_id);
  const submittedOrder = submittedItems.map((item) => item._history_id);
  const sameItemSet = initialOrder.length === submittedOrder.length
    && initialOrder.every((itemId) => submittedById.has(itemId));
  if (sameItemSet && !areEqual(initialOrder, submittedOrder)) {
    const initialPositions = new Map(initialOrder.map((itemId, index) => [itemId, index + 1]));
    const movedIds = new Set(
      submittedOrder.filter((itemId, index) => initialPositions.get(itemId) !== index + 1)
    );
    changes.push({
      field_name: "display_order",
      field_label: `${sectionLabel} Order`,
      change_type: "multiple_items",
      action: "reordered",
      previous_value: initialItems
        .map((item, index) => ({
          id: item._history_id,
          label: getRolesItemLabel(item),
          position: index + 1
        }))
        .filter((item) => movedIds.has(item.id)),
      new_value: submittedItems
        .map((item, index) => ({
          id: item._history_id,
          label: getRolesItemLabel(item),
          position: index + 1
        }))
        .filter((item) => movedIds.has(item.id))
    });
  }

  return changes.length > 0
    ? {
      section_key: "roles_responsibilities_competency",
      section_label: sectionLabel,
      changes
    }
    : null;
};

const getAlarmSection = (plan = {}) => getEmergencyPlanSection(plan, "alarm_communication_emergency_access");

const getAlarmItemLabel = (item = {}) => item.element || "Empty Element";

const getAlarmItemValue = (item = {}) => ({
  _history_id: item._history_id,
  element: item.element || "",
  required_arrangement: item.required_arrangement || ""
});

const buildAlarmChanges = (initialPlan = {}, submittedPlan = {}) => {
  const initialSection = getAlarmSection(initialPlan);
  const submittedSection = getAlarmSection(submittedPlan);
  const sectionLabel = submittedSection.display_name
    || initialSection.display_name
    || "Alarm, Communication & Emergency Access";
  const changes = [];
  const previousToggle = getBooleanValue(initialSection.data && initialSection.data.toggle);
  const newToggle = getBooleanValue(submittedSection.data && submittedSection.data.toggle);

  if (previousToggle !== newToggle) {
    changes.push({
      field_name: "toggle",
      field_label: `${sectionLabel} Toggle`,
      change_type: "inline",
      action: newToggle ? "toggled_on" : "toggled_off",
      previous_value: previousToggle,
      new_value: newToggle
    });
  }

  const initialItems = Array.isArray(initialSection.data && initialSection.data.items) ? initialSection.data.items : [];
  const submittedItems = Array.isArray(submittedSection.data && submittedSection.data.items) ? submittedSection.data.items : [];
  const initialById = new Map(initialItems.map((item) => [item._history_id, item]));
  const submittedById = new Map(submittedItems.map((item) => [item._history_id, item]));
  const itemIds = new Set([...initialById.keys(), ...submittedById.keys()]);

  itemIds.forEach((itemId) => {
    const previous = initialById.get(itemId);
    const next = submittedById.get(itemId);
    if (!previous || !next) {
      changes.push({
        field_name: "items",
        field_label: `${next ? "Added" : "Removed"} - ${getAlarmItemLabel(next || previous)}`,
        change_type: "summary",
        action: next ? "added" : "deleted",
        previous_value: next ? null : getAlarmItemValue(previous),
        new_value: next ? getAlarmItemValue(next) : null
      });
      return;
    }

    const previousValue = getAlarmItemValue(previous);
    const newValue = getAlarmItemValue(next);
    if (!areEqual(previousValue, newValue)) {
      changes.push({
        field_name: "items",
        field_label: `Updated - ${getAlarmItemLabel(next || previous)}`,
        change_type: "multiple_items",
        action: "updated",
        previous_value: previousValue,
        new_value: newValue
      });
    }
  });

  const initialOrder = initialItems.map((item) => item._history_id);
  const submittedOrder = submittedItems.map((item) => item._history_id);
  const sameItemSet = initialOrder.length === submittedOrder.length
    && initialOrder.every((itemId) => submittedById.has(itemId));
  if (sameItemSet && !areEqual(initialOrder, submittedOrder)) {
    const initialPositions = new Map(initialOrder.map((itemId, index) => [itemId, index + 1]));
    const movedIds = new Set(submittedOrder.filter((itemId, index) => initialPositions.get(itemId) !== index + 1));
    changes.push({
      field_name: "display_order",
      field_label: `${sectionLabel} Order`,
      change_type: "multiple_items",
      action: "reordered",
      previous_value: initialItems.map((item, index) => ({
        id: item._history_id,
        label: getAlarmItemLabel(item),
        position: index + 1
      })).filter((item) => movedIds.has(item.id)),
      new_value: submittedItems.map((item, index) => ({
        id: item._history_id,
        label: getAlarmItemLabel(item),
        position: index + 1
      })).filter((item) => movedIds.has(item.id))
    });
  }

  return changes.length > 0 ? {
    section_key: "alarm_communication_emergency_access",
    section_label: sectionLabel,
    changes
  } : null;
};

export const buildEmergencyPlansEditItems = (initialPlans = [], submittedPlans = []) => {
  if (!Array.isArray(initialPlans) || !Array.isArray(submittedPlans)) {
    return [];
  }

  return initialPlans.reduce((items, initialPlan, index) => {
    const submittedPlan = submittedPlans[index] || {};
    const previousCategories = getPlanCategoriesForHistory(initialPlan);
    const newCategories = getPlanCategoriesForHistory(submittedPlan);
    const changes = [];
    const sectionChanges = [];

    if (!areEqual(initialPlan && initialPlan.plan_name, submittedPlan.plan_name)) {
      changes.push({
        field_name: "plan_name",
        field_label: "Plan Name",
        change_type: "inline",
        action: "updated",
        previous_value: initialPlan && initialPlan.plan_name !== undefined
          ? initialPlan.plan_name
          : null,
        new_value: submittedPlan.plan_name !== undefined
          ? submittedPlan.plan_name
          : null
      });
    }

    if (!areEqual(previousCategories, newCategories)) {
      changes.push({
        field_name: "categories",
        field_label: "Rescue Category",
        change_type: "multiple_items",
        action: getCategoryChangeAction(previousCategories, newCategories),
        previous_value: previousCategories,
        new_value: newCategories
      });
    }

    if (changes.length > 0) {
      sectionChanges.push({
        section_key: "emergency_plan_details",
        section_label: "Emergency Plan Details",
        changes
      });
    }

    const initialRescueSection = getEmergencyPlanSection(initialPlan, "foreseeable_rescue_scenarios");
    const submittedRescueSection = getEmergencyPlanSection(submittedPlan, "foreseeable_rescue_scenarios");
    const rescueSectionLabel = submittedRescueSection.display_name
      || initialRescueSection.display_name
      || "Foreseeable Rescue Scenarios";
    const previousSectionToggle = getBooleanValue(initialRescueSection.data && initialRescueSection.data.toggle);
    const newSectionToggle = getBooleanValue(submittedRescueSection.data && submittedRescueSection.data.toggle);
    const initialRescueProcedures = getLinkedDocuments(initialPlan);
    const submittedRescueProcedures = getLinkedDocuments(submittedPlan);
    const initialProcedureById = new Map(initialRescueProcedures.map((procedure) => [`${getRescueProcedureId(procedure)}`, procedure]));
    const submittedProcedureById = new Map(submittedRescueProcedures.map((procedure) => [`${getRescueProcedureId(procedure)}`, procedure]));
    const procedureIds = new Set([...initialProcedureById.keys(), ...submittedProcedureById.keys()]);
    const rescueProcedureSelections = [];

    procedureIds.forEach((procedureId) => {
      const previousProcedure = initialProcedureById.get(procedureId);
      const newProcedure = submittedProcedureById.get(procedureId);
      const previousChecked = previousProcedure ? getBooleanValue(previousProcedure.checked) : false;
      const newChecked = newProcedure ? getBooleanValue(newProcedure.checked) : false;

      if (previousChecked === newChecked) {
        return;
      }

      const procedure = newProcedure || previousProcedure || {};
      rescueProcedureSelections.push({
        previous: getRescueProcedureHistoryValue(previousProcedure || procedure, previousChecked),
        newValue: getRescueProcedureHistoryValue(newProcedure || procedure, newChecked)
      });
    });

    if (!areEqual(previousSectionToggle, newSectionToggle)) {
      sectionChanges.push({
        section_key: "foreseeable_rescue_scenarios",
        section_label: rescueSectionLabel,
        changes: [{
          field_name: "toggle",
          field_label: "Foreseeable Rescue Scenarios Toggle",
          change_type: "inline",
          action: newSectionToggle ? "toggled_on" : "toggled_off",
          previous_value: previousSectionToggle,
          new_value: newSectionToggle
        }]
      });
    }

    const initialProcedurePositions = getRescueProcedurePositions(initialPlan);
    const submittedProcedurePositions = getRescueProcedurePositions(submittedPlan);
    const initialOrderIds = initialProcedurePositions.map(({ procedure }) => getRescueProcedureId(procedure));
    const submittedOrderIds = submittedProcedurePositions.map(({ procedure }) => getRescueProcedureId(procedure));
    const initialPositionById = new Map(initialProcedurePositions.map(({ procedure, position }) => [
      `${getRescueProcedureId(procedure)}`,
      position
    ]));
    const submittedPositionById = new Map(submittedProcedurePositions.map(({ procedure, position }) => [
      `${getRescueProcedureId(procedure)}`,
      position
    ]));
    const sameProcedureSet = initialOrderIds.length === submittedOrderIds.length
      && initialOrderIds.every((procedureId) => submittedPositionById.has(`${procedureId}`));
    const hasPositionChanges = submittedOrderIds.some((procedureId) => (
      initialPositionById.get(`${procedureId}`) !== submittedPositionById.get(`${procedureId}`)
    ));
    if (sameProcedureSet && hasPositionChanges) {
      const movedIds = new Set(
        submittedOrderIds.filter((procedureId) => (
          initialPositionById.get(`${procedureId}`) !== submittedPositionById.get(`${procedureId}`)
        ))
          .map((procedureId) => `${procedureId}`)
      );
      sectionChanges.push({
        section_key: "foreseeable_rescue_scenarios",
        section_label: rescueSectionLabel,
        changes: [{
          field_name: "display_order",
          field_label: "Rescue Procedure Order",
          change_type: "multiple_items",
          action: "reordered",
          previous_value: initialProcedurePositions
            .map(({ procedure, position }) => ({
              id: getRescueProcedureId(procedure),
              label: getRescueProcedureLabel(procedure),
              position
            }))
            .filter((procedure) => movedIds.has(`${procedure.id}`)),
          new_value: submittedProcedurePositions
            .map(({ procedure, position }) => ({
              id: getRescueProcedureId(procedure),
              label: getRescueProcedureLabel(procedure),
              position
            }))
            .filter((procedure) => movedIds.has(`${procedure.id}`))
        }]
      });
    }

    if (rescueProcedureSelections.length > 0) {
      sectionChanges.push({
        section_key: "foreseeable_rescue_scenarios",
        section_label: rescueSectionLabel,
        changes: [{
          field_name: "checked",
          field_label: "Procedure Selection",
          change_type: "multiple_items",
          action: "updated",
          previous_value: rescueProcedureSelections.map((selection) => selection.previous),
          new_value: rescueProcedureSelections.map((selection) => selection.newValue)
        }]
      });
    }

    const rescueEquipmentChanges = buildRescueEquipmentStagingChanges(initialPlan, submittedPlan);
    sectionChanges.push(...rescueEquipmentChanges);
    const rolesChanges = buildRolesChanges(initialPlan, submittedPlan);
    if (rolesChanges) {
      sectionChanges.push(rolesChanges);
    }
    const alarmChanges = buildAlarmChanges(initialPlan, submittedPlan);
    if (alarmChanges) {
      sectionChanges.push(alarmChanges);
    }

    const previousAuthorisationNotes = getEmergencyPlanAuthorisationNotes(initialPlan);
    const newAuthorisationNotes = getEmergencyPlanAuthorisationNotes(submittedPlan);
    if (!areEqual(previousAuthorisationNotes, newAuthorisationNotes)) {
      sectionChanges.push({
        section_key: "document_authorisation",
        section_label: "Document Authorisation",
        changes: [{
          field_name: "emergency_plans_authorisation_notes",
          field_label: "Authorisation Notes",
          change_type: "rich_text",
          action: "updated",
          previous_value: previousAuthorisationNotes,
          new_value: newAuthorisationNotes
        }]
      });
    }

    if (sectionChanges.length === 0) {
      return items;
    }

    if (sectionChanges.length > 0) {
      items.push({
        item_id: getEmergencyPlanId(initialPlan),
        item_label: submittedPlan.plan_name || initialPlan.plan_name || null,
        section_changes: sectionChanges
      });
    }
    return items;
  }, []);
};

const getEmergencyProcedureDocumentNumber = (procedure = {}) => (
  procedure.document_number !== undefined && procedure.document_number !== null
    ? procedure.document_number
    : null
);

export const buildEmergencyProceduresEditItems = (
  initialProcedures = [],
  submittedProcedures = []
) => {
  if (!Array.isArray(initialProcedures) || !Array.isArray(submittedProcedures)) {
    return [];
  }

  return initialProcedures.reduce((items, initialProcedure, index) => {
    const submittedProcedure = submittedProcedures[index] || {};
    if (areEqual(initialProcedure && initialProcedure.title, submittedProcedure.title)) {
      return items;
    }

    items.push({
      item_id: getEmergencyProcedureDocumentNumber(initialProcedure),
      item_label: submittedProcedure.title || initialProcedure.title || null,
      section_changes: [{
        section_key: "procedure_details",
        section_label: "Procedure details",
        changes: [{
          field_name: "title",
          field_label: "Procedure Name",
          change_type: "inline",
          action: "updated",
          previous_value: initialProcedure && initialProcedure.title !== undefined
            ? initialProcedure.title
            : null,
          new_value: submittedProcedure.title !== undefined
            ? submittedProcedure.title
            : null
        }]
      }]
    });

    return items;
  }, []);
};

const getReadinessHeading = (headingContents = {}) => (
  headingContents && typeof headingContents.rescue_readiness_checklist_heading === "string"
    ? headingContents.rescue_readiness_checklist_heading
    : ""
);

export const buildReadinessEditItems = (
  initialHeadingContents = {},
  submittedHeadingContents = {}
) => {
  const previousValue = getReadinessHeading(initialHeadingContents);
  const newValue = getReadinessHeading(submittedHeadingContents);

  if (areEqual(previousValue, newValue)) {
    return [];
  }

  return [{
    item_id: null,
    item_label: null,
    section_changes: [{
      section_key: "readiness_questions",
      section_label: "Rescue Readiness Questions",
      changes: [{
        field_name: "rescue_readiness_checklist_heading",
        field_label: "Rescue Readiness Checklist Heading",
        change_type: "rich_text",
        action: "updated",
        previous_value: previousValue,
        new_value: newValue
      }]
    }]
  }];
};

const DOCUMENT_PROJECT_DETAILS_FIELD_LABELS = {
  swms_name: "SWMS Name",
  po_number: "PO / WO",
  issue_date: "Issue Date",
  site_name: "Site Name",
  site_address: "Site Address",
  "company_details.company_name": "Company Name",
  "company_details.company_abn": "Company ABN",
  "company_details.company_address": "Company Address",
  "company_details.company_email": "Company Email",
  "company_details.company_phone": "Company Phone",
  "company_details.company_website": "Company Website",
  "client_details.client_name": "Client Name",
  "client_details.client_abn": "Client ABN",
  "client_details.client_address": "Client Address",
  "client_details.client_contact_person_name": "Client Rep",
  "client_details.client_contact_email": "Client Email",
  "client_details.client_contact_phone": "Client Phone",
  "client_details.client_contact_role": "Client Position"
};

const getNestedSectionValue = (section, sectionKey, key) => (
  section && section.data && section.data[sectionKey]
    ? section.data[sectionKey][key]
    : undefined
);

const getFieldValue = (data, fieldName) => {
  const fieldParts = fieldName.split(".");
  return fieldParts.reduce(
    (value, fieldPart) => (value === undefined || value === null ? undefined : value[fieldPart]),
    data
  );
};

const NESTED_PROJECT_DETAILS_FIELDS = [
  "company_details.company_name",
  "company_details.company_abn",
  "company_details.company_address",
  "company_details.company_email",
  "company_details.company_phone",
  "company_details.company_website",
  "client_details.client_name",
  "client_details.client_abn",
  "client_details.client_address",
  "client_details.client_contact_person_name",
  "client_details.client_contact_email",
  "client_details.client_contact_phone",
  "client_details.client_contact_role"
];

const getCollectionChangeAction = (previousValue, newValue) => {
  if (!Array.isArray(previousValue) || !Array.isArray(newValue)) {
    return "updated";
  }

  const previousItems = new Set(previousValue);
  const newItems = new Set(newValue);
  const hasAddedItems = newValue.some((item) => !previousItems.has(item));
  const hasDeletedItems = previousValue.some((item) => !newItems.has(item));

  if (hasAddedItems && !hasDeletedItems) {
    return "added";
  }

  if (hasDeletedItems && !hasAddedItems) {
    return "deleted";
  }

  return "updated";
};

const getFileCollectionChangeAction = (previousValue, newValue) => {
  if (!Array.isArray(previousValue) || !Array.isArray(newValue)) {
    return "updated";
  }

  const getFileIdentity = (file) => {
    if (!file || typeof file !== "object") {
      return JSON.stringify(file);
    }
    const fileName = file.file_name || {};
    return fileName.file_name || fileName.visible_name || JSON.stringify(file);
  };
  const previousFiles = new Set(previousValue.map(getFileIdentity));
  const newFiles = new Set(newValue.map(getFileIdentity));
  const hasAddedFiles = newValue.some((file) => !previousFiles.has(getFileIdentity(file)));
  const hasDeletedFiles = previousValue.some((file) => !newFiles.has(getFileIdentity(file)));

  if (hasAddedFiles && !hasDeletedFiles) {
    return "added";
  }

  if (hasDeletedFiles && !hasAddedFiles) {
    return "deleted";
  }

  return "updated";
};

const getAttachmentValue = (section, key) => (
  getNestedSectionValue(section, "attachments", key)
);

const getSectionFromDocument = (document, sectionKey) => (
  document
  && document.sections
  && document.sections[sectionKey]
    ? document.sections[sectionKey]
    : null
);

const getHighRiskActivities = (document) => {
  const section = getSectionFromDocument(document, "high_risk_activities");
  return section && Array.isArray(section.data) ? section.data : [];
};

const getHighRiskChangesByCategory = (initialDocument, submittedDocument) => {
  const initialCategories = getHighRiskActivities(initialDocument);
  const submittedCategories = getHighRiskActivities(submittedDocument);
  const categoryNames = Array.from(new Set([
    ...initialCategories.map((category) => category.category_name),
    ...submittedCategories.map((category) => category.category_name)
  ].filter(Boolean)));

  return categoryNames.reduce((categoryChanges, categoryName) => {
    const initialCategory = initialCategories.find((category) => category.category_name === categoryName) || {};
    const submittedCategory = submittedCategories.find((category) => category.category_name === categoryName) || {};
    const initialActivities = Array.isArray(initialCategory.activities) ? initialCategory.activities : [];
    const submittedActivities = Array.isArray(submittedCategory.activities) ? submittedCategory.activities : [];
    const initialById = new Map(initialActivities.map((activity) => [activity.id, activity]));
    const submittedById = new Map(submittedActivities.map((activity) => [activity.id, activity]));
    const activityIds = new Set([...initialById.keys(), ...submittedById.keys()]);
    const added = [];
    const deleted = [];

    activityIds.forEach((activityId) => {
      const initialActivity = initialById.get(activityId);
      const submittedActivity = submittedById.get(activityId);
      const initialSelected = initialActivity ? Number(initialActivity.selected) : 0;
      const submittedSelected = submittedActivity ? Number(submittedActivity.selected) : 0;

      if (initialSelected === submittedSelected) {
        return;
      }

      if (submittedSelected === 1) {
        added.push(submittedActivity);
      } else {
        deleted.push(initialActivity);
      }
    });

    if (added.length === 0 && deleted.length === 0) {
      return categoryChanges;
    }

    categoryChanges.push({
      field_name: categoryName,
      field_label: categoryName,
      change_type: "multiple_items",
      action: added.length > 0 && deleted.length === 0
        ? "added"
        : (deleted.length > 0 && added.length === 0 ? "deleted" : "updated"),
      previous_value: deleted,
      new_value: added
    });

    return categoryChanges;
  }, []);
};

export const buildDocumentProjectDetailsChanges = (
  initialValues = {},
  submittedValues = {},
  initialDocument = {},
  submittedDocument = {}
) => {
  const initialData = getSectionData(initialValues, "document_project_details");
  const submittedData = getSectionData(submittedValues, "document_project_details");
  const submittedSection = submittedDocument
    && submittedDocument.sections
    && submittedDocument.sections.document_project_details;
  const initialSection = initialDocument
    && initialDocument.sections
    && initialDocument.sections.document_project_details;
  const sectionLabel = submittedSection && submittedSection.display_name;
  const fieldNames = Array.from(new Set([
    ...Object.keys(initialData),
    ...Object.keys(submittedData)
  ])).filter((fieldName) => (
    fieldName !== "scope_of_work"
    && fieldName !== "qli_areas"
    && fieldName !== "areas"
    && fieldName !== "attachments"
    && fieldName !== "company_details"
    && fieldName !== "client_details"
  ));
  const changes = fieldNames
    .filter((fieldName) => {
      const initialValue = fieldName === "issue_date"
        ? initialSection && initialSection.data && initialSection.data.issue_date
        : initialData[fieldName];
      const submittedValue = fieldName === "issue_date"
        ? submittedSection && submittedSection.data && submittedSection.data.issue_date
        : submittedData[fieldName];
      return !areEqual(initialValue, submittedValue);
    })
    .map((fieldName) => ({
      field_name: fieldName,
      field_label: DOCUMENT_PROJECT_DETAILS_FIELD_LABELS[fieldName] || fieldName,
      change_type: "inline",
      action: "updated",
      previous_value: fieldName === "issue_date"
        ? (initialSection && initialSection.data && initialSection.data.issue_date) || null
        : (initialData[fieldName] === undefined ? null : initialData[fieldName]),
      new_value: fieldName === "issue_date"
        ? (submittedSection && submittedSection.data && submittedSection.data.issue_date) || null
        : (submittedData[fieldName] === undefined ? null : submittedData[fieldName])
    }));

  NESTED_PROJECT_DETAILS_FIELDS.forEach((fieldName) => {
    const initialValue = getFieldValue(initialData, fieldName);
    const submittedValue = getFieldValue(submittedData, fieldName);
    if (!areEqual(initialValue, submittedValue)) {
      changes.push({
        field_name: fieldName,
        field_label: DOCUMENT_PROJECT_DETAILS_FIELD_LABELS[fieldName],
        change_type: "inline",
        action: "updated",
        previous_value: initialValue === undefined ? null : initialValue,
        new_value: submittedValue === undefined ? null : submittedValue
      });
    }
  });

  const initialToggle = getNestedSectionValue(initialSection, "scope_of_work", "toggle");
  const submittedToggle = getNestedSectionValue(submittedSection, "scope_of_work", "toggle");
  if (!areEqual(initialToggle, submittedToggle)) {
    changes.push({
      field_name: "scope_of_work.toggle",
      field_label: "Scope of Work Toggle",
      change_type: "inline",
      action: submittedToggle ? "toggled_on" : "toggled_off",
      previous_value: initialToggle === undefined ? null : initialToggle,
      new_value: submittedToggle === undefined ? null : submittedToggle
    });
  }

  const initialScopeValue = getNestedSectionValue(initialSection, "scope_of_work", "value");
  const submittedScopeValue = getNestedSectionValue(submittedSection, "scope_of_work", "value");
  if (!areEqual(initialScopeValue, submittedScopeValue)) {
    changes.push({
      field_name: "scope_of_work.value",
      field_label: "Scope of Work",
      change_type: "inline",
      action: "updated",
      previous_value: initialScopeValue === undefined ? null : initialScopeValue,
      new_value: submittedScopeValue === undefined ? null : submittedScopeValue
    });
  }

  const initialQliToggle = getNestedSectionValue(initialSection, "qli_areas", "toggle");
  const submittedQliToggle = getNestedSectionValue(submittedSection, "qli_areas", "toggle");
  if (!areEqual(initialQliToggle, submittedQliToggle)) {
    changes.push({
      field_name: "qli_areas.toggle",
      field_label: "QLI Areas Toggle",
      change_type: "inline",
      action: submittedQliToggle ? "toggled_on" : "toggled_off",
      previous_value: initialQliToggle === undefined ? null : initialQliToggle,
      new_value: submittedQliToggle === undefined ? null : submittedQliToggle
    });
  }

  const initialAreasToggle = getNestedSectionValue(initialSection, "areas", "toggle");
  const submittedAreasToggle = getNestedSectionValue(submittedSection, "areas", "toggle");
  if (!areEqual(initialAreasToggle, submittedAreasToggle)) {
    changes.push({
      field_name: "areas.toggle",
      field_label: "Areas Toggle",
      change_type: "inline",
      action: submittedAreasToggle ? "toggled_on" : "toggled_off",
      previous_value: initialAreasToggle === undefined ? null : initialAreasToggle,
      new_value: submittedAreasToggle === undefined ? null : submittedAreasToggle
    });
  }

  const initialAreasValues = getNestedSectionValue(initialSection, "areas", "values");
  const submittedAreasValues = getNestedSectionValue(submittedSection, "areas", "values");
  if (!areEqual(initialAreasValues, submittedAreasValues)) {
    changes.push({
      field_name: "areas.values",
      field_label: "Areas",
      change_type: "multiple_items",
      action: getCollectionChangeAction(initialAreasValues, submittedAreasValues),
      previous_value: initialAreasValues === undefined ? null : initialAreasValues,
      new_value: submittedAreasValues === undefined ? null : submittedAreasValues
    });
  }

  const initialAttachmentsToggle = getAttachmentValue(initialSection, "toggle");
  const submittedAttachmentsToggle = getAttachmentValue(submittedSection, "toggle");
  if (!areEqual(initialAttachmentsToggle, submittedAttachmentsToggle)) {
    changes.push({
      field_name: "attachments.toggle",
      field_label: "Photos & Attachments Toggle",
      change_type: "inline",
      action: submittedAttachmentsToggle ? "toggled_on" : "toggled_off",
      previous_value: initialAttachmentsToggle === undefined ? null : initialAttachmentsToggle,
      new_value: submittedAttachmentsToggle === undefined ? null : submittedAttachmentsToggle
    });
  }

  const initialAttachmentsValue = getAttachmentValue(initialSection, "value");
  const submittedAttachmentsValue = getAttachmentValue(submittedSection, "value");
  if (!areEqual(initialAttachmentsValue, submittedAttachmentsValue)) {
    changes.push({
      field_name: "attachments.value",
      field_label: "Photos & Attachments",
      change_type: "multiple_files",
      action: getFileCollectionChangeAction(initialAttachmentsValue, submittedAttachmentsValue),
      previous_value: initialAttachmentsValue === undefined ? null : initialAttachmentsValue,
      new_value: submittedAttachmentsValue === undefined ? null : submittedAttachmentsValue
    });
  }

  const initialAuthorizationSection = initialDocument
    && initialDocument.sections
    && initialDocument.sections.document_authorization;
  const submittedAuthorizationSection = submittedDocument
    && submittedDocument.sections
    && submittedDocument.sections.document_authorization;
  const initialAuthorizationNotes = initialAuthorizationSection
    && initialAuthorizationSection.data
    ? initialAuthorizationSection.data.notes
    : undefined;
  const submittedAuthorizationNotes = submittedAuthorizationSection
    && submittedAuthorizationSection.data
    ? submittedAuthorizationSection.data.notes
    : undefined;
  const authorizationChanges = [];

  if (!areEqual(initialAuthorizationNotes, submittedAuthorizationNotes)) {
    authorizationChanges.push({
      field_name: "notes",
      field_label: "Authorisation Notes",
      change_type: "rich_text",
      action: "updated",
      previous_value: initialAuthorizationNotes === undefined ? null : initialAuthorizationNotes,
      new_value: submittedAuthorizationNotes === undefined ? null : submittedAuthorizationNotes
    });
  }

  const items = [];
  if (changes.length > 0) {
    items.push({
    item_id: null,
    item_label: null,
    section_changes: [{
      section_key: "document_project_details",
      section_label: sectionLabel,
      changes
    }]
    });
  }
  if (authorizationChanges.length > 0) {
    items.push({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "document_authorization",
        section_label: submittedAuthorizationSection && submittedAuthorizationSection.display_name,
        changes: authorizationChanges
      }]
    });
  }
  const highRiskChanges = getHighRiskChangesByCategory(initialDocument, submittedDocument);
  if (highRiskChanges.length > 0) {
    items.push({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "high_risk_activities",
        section_label: submittedDocument
          && getSectionFromDocument(submittedDocument, "high_risk_activities")
          && getSectionFromDocument(submittedDocument, "high_risk_activities").display_name,
        changes: highRiskChanges
      }]
    });
  }
  return items;
};

export const SWMS_EDIT_CONTEXT_SCHEMA_VERSION = 1;
export const SWMS_DOCUMENT_TYPE = "swms";

export const createSwmsEditContext = ({
  initialValues = {},
  submittedValues = {},
  internalRevisionId = 0,
  documentName = null,
  documentId = null,
  documentType = SWMS_DOCUMENT_TYPE,
  items = []
} = {}) => {
  if (areEqual(initialValues, submittedValues)) {
    return null;
  }

  return {
    schema_version: SWMS_EDIT_CONTEXT_SCHEMA_VERSION,
    internal_revision_id: Number.isInteger(internalRevisionId) ? internalRevisionId : 0,
    document_type: documentType,
    document_name: documentName,
    document_id: documentId,
    items
  };
};

export const hasMeaningfulSwmsEditContext = (editContext) => (
  Boolean(editContext)
  && Array.isArray(editContext.items)
  && editContext.items.some((item) => (
    item
    && Array.isArray(item.section_changes)
    && item.section_changes.some((section) => (
      section && Array.isArray(section.changes) && section.changes.length > 0
    ))
  ))
);

export const isSwmsEditContextFieldPresent = (value, key) => hasOwn(value, key);
